import { appConfig } from '@/config/app-config';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
// profile/index.ts
import { Module, MutationTree, GetterTree, ActionTree } from 'vuex';
import { TenantState, Center } from '@core/interfaces/tenant-state';
import { RootState } from '@core/interfaces/root-state';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';

import BPCApiServiceInstance from '@core/services/bpc-api-service.instance';
import { bpcApi } from '@core/services/bpc-api.service';

const bpcApiV2 = new BPCApiServiceInstance({ apiPath: appConfig.apiPathV2 });
const bpcRouterHelper = new BPCApiRouteHelper();
// Initial state
const state: TenantState = {
  tenants: [],
  tenant: null,
  selectedTenantId: null,
  loading: false,
  tenantAlias: null,
};

// Synchronous functions to get data from state (can be composed properties)
const getters: GetterTree<TenantState, RootState> = {
  tenants(state): Center[] {
    return state.tenants;
  },
  selectedTenant(state): Center {
    if (state.tenants && state.selectedTenantId && state.tenant.id === state.selectedTenantId) {
      return state.tenant;
    }
    return null;
  },
  selectedTenantId(state): number {
    return Number(state.selectedTenantId);
  },
  isLoading(state): boolean {
    return !!state.loading;
  },

  tenantAlias(state): string {
    return state.tenantAlias;
  },
  tenantFrontOfficeV2Enabled(state, getters): boolean {
    return !!getters.selectedTenant?.frontoffice_v2_activated || false;
  },
};

// Asynchornous functions to get data usually from an api call
const actions: ActionTree<TenantState, RootState> = {
  initializeTenantByAlias({ commit }, alias: string) {
    return new Promise((resolve, reject) => {
      bpcApi.get(`centers/${alias}`).subscribe(
        (response: BPCGenericApiResponse) => {
          console.log('initializeTenantByAlias V1', response);
        },
        error => {
          console.log(error);
        },
      );
      bpcApiV2.get(`centers/${alias}`).subscribe(
        (response: BPCGenericApiResponse) => {
          commit('selectTenantId', response.data.id);
          commit('storeTenants', [response.data]);
          commit('storeTenant', response.data);
          commit('setTenantAlias', response.data.alias);
          commit('centerSettings/updateCenterSettings', response.data.center_setting, {
            root: true,
          });
          resolve(response.data);
        },
        error => {
          reject(error);
        },
      );
    });
  },
  getTenants({ commit }): void {
    commit('startLoading');
    bpcApi.get(bpcRouterHelper.getMyTenantsPath(), {}, 'admin').subscribe(
      (response: BPCGenericApiResponse) => {
        console.log('getTenants: V1', response);
      },
      error => {
        console.log(error);
      },
    );
    bpcApiV2.get(`centers/my-centers`).subscribe(
      (res: BPCGenericApiResponse) => {
        commit('storeTenants', res.data);
      },
      error => {
        console.log(error);
        commit('stopLoading');
      },
    );
  },
};

// The vuex way of changing a state property
export const mutations: MutationTree<TenantState> = {
  stopLoading(state) {
    state.loading = false;
  },
  startLoading(state) {
    state.loading = true;
  },

  storeTenants(state, data: Center[]) {
    state.tenants = data;
  },
  storeTenant(state, data: Center) {
    if (data?.center_setting) {
      // NOTE now selected tenant do not have center_setting
      // access to center_setting via store.getters['centerSettings/centerSettings']
      // remove center_setting from tenant stored in localstorage
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { center_setting, ...dataWithoutSettings } = data;

      state.tenant = dataWithoutSettings;
      localStorage.setItem('tenant', JSON.stringify(dataWithoutSettings));
    } else {
      state.tenant = data;
      localStorage.setItem('tenant', JSON.stringify(data));
    }
  },
  setTenantAlias(state, tenantAlias) {
    if (tenantAlias && tenantAlias !== appConfig.globalTenantAlias) {
      state.tenantAlias = tenantAlias;
      localStorage.setItem('tenantAlias', tenantAlias);
    } else {
      state.tenantAlias = null;
      localStorage.removeItem('tenantAlias');
    }
  },
  selectTenant(state, data: Center) {
    if (data.alias && data.alias !== appConfig.globalTenantAlias) {
      localStorage.setItem('tenant_id', `${data.id}`);
      localStorage.setItem('tenantAlias', `${data.alias}`);
      state.tenantAlias = data.alias;
      state.selectedTenantId = data.id;
    }
  },
  selectTenantId(state, data: number) {
    localStorage.setItem('tenant_id', `${data}`);
    state.selectedTenantId = data;
  },

  clearTenants(state) {
    state.tenants = null;
  },

  clearSelectedTenant(state) {
    state.selectedTenantId = null;
    state.tenant = null;
    localStorage.removeItem('tenant_id');
  },
  clearTenantAlias(state) {
    state.tenantAlias = null;
    localStorage.removeItem('tenantAlias');
  },
};

export const TenantStoreModule: Module<TenantState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
